// frontend/src/components/PreMatchSetup.js
import React, { useEffect, useState } from "react";
import apiClient from "../services/apiClient";
import { useParams, useNavigate } from "react-router-dom";
import { useTheme } from '../config/ThemeContext';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";
 
function SetupSkeleton() {
  return (
    <div className="text-center py-12 text-gray-500 animate-pulse">Loading match setup...</div>
  );
}

const PreMatchSetup = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const colors = useTheme(); // Get current theme	
  const [matchDetails, setMatchDetails] = useState(null);
  const [matchState, setMatchState] = useState(null);
  const [sets, setSets] = useState([]);
  const [referees, setReferees] = useState("");
  const [spectators, setSpectators] = useState("");
  const [maxSetsToWin, setMaxSetsToWin] = useState("");
  const [noTTOOnLastSet, setNoTTOOnLastSet] = useState(true);
  const [finalSetPoints, setFinalSetPoints] = useState("");

  useEffect(() => {
    const fetchMatchData = async () => {
      try {
        const [
          { data: fetchedMatchDetails },
          { data: fetchedMatchState },
          { data: fetchedSets }
        ] = await Promise.all([
          apiClient.get(`/api/matches/${matchId}`),
          apiClient.get(`/api/matches/${matchId}/matchState`),
          apiClient.get(`/api/matches/${matchId}/sets`)
        ]);
        setMatchDetails(fetchedMatchDetails);
        setMatchState(fetchedMatchState);
        setSets(Array.isArray(fetchedSets) ? fetchedSets : []);
        setReferees(fetchedMatchDetails.referees || "");
        setSpectators(fetchedMatchDetails.spectators || "");
        setMaxSetsToWin(fetchedMatchDetails.rules?.maxSetsToWin || "");
        setNoTTOOnLastSet(
          typeof fetchedMatchDetails.rules?.noTTOOnLastSet === "boolean"
            ? fetchedMatchDetails.rules.noTTOOnLastSet
            : true
        );
        setFinalSetPoints(fetchedMatchDetails.rules?.finalSetWinningPoints || "");
      } catch (err) {
        console.error("Failed to load match data:", err);
      }
    };
    fetchMatchData();
  }, [matchId]);

const handleStartMatch = async () => {
  try {
    await Promise.all([
      apiClient.put(`/api/matches/${matchId}/pre-match-notes`, {
        referees,
        spectators: spectators === "" ? null : parseInt(spectators, 10),
        rules: {
          maxSetsToWin: maxSetsToWin !== "" ? parseInt(maxSetsToWin, 10) : undefined,
          noTTOOnLastSet,
          finalSetWinningPoints: finalSetPoints !== "" ? parseInt(finalSetPoints, 10) : undefined,
        }
      }),
      apiClient.post(`/api/matches/${matchId}/start`)
    ]);

    navigate(`/matches/pre-set/${matchId}/1`);
  } catch (err) {
    console.error("Failed to start match:", err);
  }
};


  if (!matchDetails || !matchState) {
    return <SetupSkeleton />;
  }

  const handleViewReport = () => {
    window.open(`${API_BASE_URL}/api/matches/${matchId}/report`, "_blank");
  };

  const renderActionButton = () => {
    if (!matchDetails || !matchState) return null;

    const matchStatus = matchDetails.status;

    if (matchStatus === "Not Started") {
      return (
        <button className={colors.buttons.primary} onClick={handleStartMatch}>
          🏐 Start Match
        </button>
      );
    }

    if (matchStatus === "Completed" || matchState.matchOver) {
      return (
        <button className={colors.buttons.primary} onClick={handleViewReport}>
          📋 View Report
        </button>
      );
    }

    if (sets.length > 0) {
      const lastSet = sets[sets.length - 1];
      if (lastSet.status === "In Progress") {
        return (
          <button
            className={colors.buttons.primary}
            onClick={() =>
              navigate(`/live-scoring/${matchId}/${lastSet.setNumber}`)
            }
          >
            Continue Live Scoring for Set {lastSet.setNumber}
          </button>
        );
      }

      const nextSetNumber = sets.length + 1;
      return (
        <button
          className={colors.buttons.primary}
          onClick={() =>
            navigate(`/matches/pre-set/${matchId}/${nextSetNumber}`)
          }
        >
          Start Set {nextSetNumber}
        </button>
      );
    }

    // No sets yet
    return (
      <button
        className={colors.buttons.primary}
        onClick={() => navigate(`/matches/pre-set/${matchId}/1`)}
      >
        Start Set 1
      </button>
    );
  };

return (
  <div className="max-w-4xl mx-auto px-4 py-6">
    {matchDetails && (
      <div className={`bg-white p-6 rounded-xl shadow-md border-t-4 ${colors.primary.border} mb-6`}>
        <header className="mb-4">
          <h3 className={`text-2xl font-bold ${colors.neutral.text}`}>
            {matchDetails.matchTitle}
            <span className="block text-sm font-normal text-gray-500">
              {new Date(matchDetails.startTime).toLocaleDateString()}
            </span>
          </h3>
          <p className={`text-sm mt-2 ${colors.neutral.lightText}`}>
            <span className="font-semibold text-gray-700">Location:</span>{" "}
            {matchDetails.city} – {matchDetails.hall}
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Referees</label>
            <input
              type="text"
              value={referees}
              onChange={(e) => setReferees(e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="Enter the names of the referees"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Spectators</label>
            <input
              type="number"
              value={spectators}
              onChange={(e) => setSpectators(e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="e.g. 150"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sets Needed to Win</label>
            <input
              type="number"
              value={maxSetsToWin}
              onChange={(e) => setMaxSetsToWin(e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="e.g. 3"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Final Set Points</label>
            <input
              type="number"
              value={finalSetPoints}
              onChange={(e) => setFinalSetPoints(e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="e.g. 15"
            />
          </div>
		            <div className="flex items-center mt-6 md:mt-0">
            <input
              id="ttoFinal"
              type="checkbox"
              checked={!noTTOOnLastSet}
              onChange={(e) => setNoTTOOnLastSet(!e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="ttoFinal" className="text-sm font-medium text-gray-700">Allow technical timeout on Final Set</label>
          </div>
        </div>

        <p
          className={`text-sm mt-6 font-medium ${
            matchDetails.status === "Completed"
              ? "text-green-600"
              : matchDetails.status === "Scheduled"
              ? "text-blue-600"
              : "text-gray-600"
          }`}
        >
          Match Status: {matchDetails.status}
        </p>
      </div>
    )}

    <div className="text-center">{renderActionButton()}</div>
  </div>
);

}

export default PreMatchSetup;